import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { Mail, Lock } from 'lucide-react';

import '../../css/components/Auth/LoginForm.css';

const LoginForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const { login } = useAuth();
  const [usernameOrEmail, setUsernameOrEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Load saved credentials on component mount
  useEffect(() => {
    const savedCredentials = localStorage.getItem('rememberedCredentials');
    if (savedCredentials) {
      try {
        const credentials = JSON.parse(savedCredentials);
        setUsernameOrEmail(credentials.usernameOrEmail || '');
        setPassword(credentials.password || '');
        setRememberMe(true);
      } catch (error) {
        console.error('Error loading saved credentials:', error);
        // Clear corrupted data
        localStorage.removeItem('rememberedCredentials');
      }
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!usernameOrEmail || !password) {
      setError(t('validation.required', 'auth'));
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      console.log('🔐 LoginForm - Starting login process...');
      console.log('🔐 LoginForm - Username/Email:', usernameOrEmail);
      console.log('🔐 LoginForm - Login function available:', !!login);
      console.log('🔐 LoginForm - Login function type:', typeof login);
      
      // Real login with backend API
      if (login && typeof login === 'function') {
        console.log('🔐 LoginForm - Calling login function...');
        const result = await login(usernameOrEmail, password);
        console.log('🔐 LoginForm - Login result:', result);
        
        if (result.success) {
          console.log('🔐 LoginForm - Login successful:', result.user);
          
          // Handle remember me functionality
          if (rememberMe) {
            // Save credentials to localStorage
            localStorage.setItem('rememberedCredentials', JSON.stringify({
              usernameOrEmail,
              password
            }));
          } else {
            // Clear saved credentials if remember me is unchecked
            localStorage.removeItem('rememberedCredentials');
          }
          
          // Redirect to intended destination or default to fullchat
          const from = location.state?.from?.pathname || '/fullchat';
          console.log('🔐 LoginForm - Redirecting to:', from);
          navigate(from);
        } else {
          console.error('🔐 LoginForm - Login failed:', result.error);
          // Use the error message from AuthContext (which now has better error handling)
          setError(result.error || 'Login failed. Please check your credentials and try again.');
        }
      } else {
        console.error('🔐 LoginForm - Login function not available');
        setError('Login function not available');
      }
    } catch (error) {
      console.error('🔐 LoginForm - Login error:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Login failed. Please try again.';
      
      if (error.message) {
        errorMessage = error.message;
      } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (error.name === 'AbortError') {
        errorMessage = 'Request timed out. Please try again.';
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-form-container">
      <h2 className="login-title">{t('auth.login')}</h2>
      <p className="login-subtitle">{t('common.welcome')}</p>
      
      <form onSubmit={handleSubmit} className="login-form" noValidate>
        {error && (
          <div className="login-error-message">
            {error}
          </div>
        )}
        
        <div className="form-group">
          <label htmlFor="usernameOrEmail">
            <Mail size={16} />
            {t('auth.usernameOrEmail')}
          </label>
          <input
            type="text"
            id="usernameOrEmail"
            name="usernameOrEmail"
            value={usernameOrEmail}
            onChange={(e) => setUsernameOrEmail(e.target.value)}
            placeholder={t('auth.enterUsernameOrEmail')}
            required
            disabled={isLoading}
            className="form-input"
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="password">
            <Lock size={16} />
            {t('auth.password')}
          </label>
          <input
            type="password"
            id="password"
            name="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('auth.password')}
            required
            disabled={isLoading}
            className="form-input"
          />
        </div>
        
        <div className="form-group remember-me-group">
          <label htmlFor="rememberMe" className="remember-me-label">
            <input
              type="checkbox"
              id="rememberMe"
              name="rememberMe"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              disabled={isLoading}
              className="remember-me-checkbox"
            />
            <span className="remember-me-text">{t('auth.rememberMe')}</span>
          </label>
        </div>
        
        <button 
          type="submit" 
          className="login-submit-button"
          disabled={isLoading}
        >
          {isLoading ? t('auth.signIn') + '...' : t('auth.signIn')}
        </button>
      </form>
      
      <div className="login-footer">
        <p>{t('auth.dontHaveAccount')} <a href="/signup" className="login-signup-link">{t('auth.signUp')}</a></p>
      </div>
    </div>
  );
};

export default LoginForm; 